import { atom } from 'nanostores';
import { apiGetProfile, apiUpdateProfile } from '~/lib/api/user-settings';
import type { UserProfile } from '@shared/types/user-settings';

interface Profile {
  username: string;
  bio: string;
  avatar: string;
}

const initialProfile: Profile = {
  username: '',
  bio: '',
  avatar: '',
};

export const profileStore = atom<Profile>(initialProfile);

export const updateProfile = (updates: Partial<Profile>) => {
  // Optimistic update
  profileStore.set({ ...profileStore.get(), ...updates });

  // Persist to server
  const serverPatch: Partial<UserProfile> = {};

  if (updates.username !== undefined) {
    serverPatch.displayName = updates.username;
  }

  if (updates.bio !== undefined) {
    serverPatch.bio = updates.bio;
  }

  if (updates.avatar !== undefined) {
    serverPatch.avatarUrl = updates.avatar;
  }

  if (Object.keys(serverPatch).length > 0) {
    apiUpdateProfile(serverPatch).catch((err) => console.error('Failed to sync profile:', err));
  }
};

/**
 * Load profile from the server. Called lazily when the settings panel opens.
 */
export async function loadProfileFromServer(): Promise<void> {
  try {
    const profile = await apiGetProfile();
    profileStore.set({
      username: profile.displayName || '',
      bio: profile.bio || '',
      avatar: profile.avatarUrl || '',
    });
  } catch (err) {
    console.error('Failed to load profile from server:', err);
  }
}

/**
 * One-time migration: push localStorage profile to server, then clear.
 */
export async function migrateProfileFromLocalStorage(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }

  const stored = localStorage.getItem('dunea_profile');
  const storedUserProfile = localStorage.getItem('dunea_user_profile');

  if (!stored && !storedUserProfile) {
    return;
  }

  try {
    const patch: Partial<UserProfile> = {};

    if (stored) {
      const parsed = JSON.parse(stored);

      if (parsed.username) {
        patch.displayName = parsed.username;
      }

      if (parsed.bio) {
        patch.bio = parsed.bio;
      }

      if (parsed.avatar) {
        patch.avatarUrl = parsed.avatar;
      }
    }

    if (Object.keys(patch).length > 0) {
      await apiUpdateProfile(patch);
    }
  } catch (err) {
    console.error('Failed to migrate profile from localStorage:', err);
  }

  // Clean up localStorage regardless
  localStorage.removeItem('dunea_profile');
  localStorage.removeItem('dunea_user_profile');
}
